import { ChevronRight, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_BRAND_ID, type ChannelModelRecord } from '@nadoutong/contracts';
import { useChannelModelsList } from '../api/useChannelModels.ts';
import { useChannelsList } from '../api/useChannels.ts';
import { useModelsList } from '../api/useModels.ts';
import { useSalesCosts } from '../api/salesCosts.ts';
import {
  procurementRangeForTargets,
  useProcurementCostIndex,
  type ProcurementCostIndex,
  type ProcurementTargetPin,
} from '../api/skuProcurementCosts.ts';
import { useSkuReadiness } from '../api/skuReadiness.ts';
import { useRoutingDefaultSkuTargetsMap } from '../api/useTenantRouting.ts';
import { useUpstreamsList } from '../api/useUpstreams.ts';
import { ROLE_OPS_ADMIN, hasAnyOpsRole } from '../auth/opsRoles.ts';
import { NewSkuDialog } from '../components/market/NewSkuDialog.tsx';
import { formatRateMultiplier } from '../components/formatters.ts';
import { coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT, type I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  PageHeader,
  Skeleton,
  Table,
  Tabs,
  TabsList,
  TabsTrigger,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * Model Market (epic #1327 PR-8): one row per marketplace SKU showing the
 * full economic chain the operator prices against —
 *
 *   procurement cost → (+ platform minimum margin) → sales cost floor
 *                    → (+ sales-allocatable profit) → sales guide rate
 *
 * — plus a readiness chip. Raw upstream cost and the brand column are
 * gone: the first is superseded by the model-granular procurement
 * multiplier (#1329), the second belongs to the namespace catalog.
 *
 * Only the sales guide rate is editable from here (via the detail page);
 * the sales cost floor is the sales lead's to maintain in LeadPricingPage
 * (#1032 E3-P1) and procurement cost is the upstream page's (#1328).
 */

const ALL_NAMESPACES = 'all';

/** `×1.2500`, or an em-dash when the value is absent. */
function formatMultiplier(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? `×${formatRateMultiplier(value)}`
    : '—';
}

/**
 * Readiness across the four concerns a SKU must satisfy to sell:
 * pricing, binding, probe, visibility. Pricing has two legs — the
 * tenant-facing sales rate (platform catalog) and the procurement cost
 * the gateway bills against — and the second is only meaningful once the
 * SKU has supply, so it is evaluated after binding and probe rather than
 * reporting a missing procurement price for a SKU bound to nothing.
 */
interface ReadinessInput {
  /**
   * The platform's canonical "a `model_pricing` row is in force", published on
   * the catalog record since #1407. Deliberately not `rate_multiplier > 0`:
   * that reads the tenant markup rather than the list price, and since the
   * write paths already refuse a multiplier `<= 0` it was true for every SKU
   * that exists. The shelf gate enforces THIS predicate, so a row that shows
   * ready here is one the server will actually let onto the shelf.
   */
  readonly priced: boolean;
  readonly visible: boolean;
  readonly bits: {
    readonly binding: boolean;
    readonly probe: boolean;
    readonly cost: boolean;
  } | undefined;
}

/** Callers must resolve absent bits first; see ReadinessCell. */
function firstUnmetReadiness(
  input: ReadinessInput & { readonly bits: NonNullable<ReadinessInput['bits']> },
): I18nKey | null {
  if (!input.priced) return 'sku_market.readiness.no_rate';
  if (!input.bits.binding) return 'sku_market.readiness.not_bound';
  if (!input.bits.probe) return 'sku_market.readiness.probe_failing';
  if (!input.bits.cost) return 'sku_market.readiness.no_procurement_cost';
  if (!input.visible) return 'sku_market.readiness.not_visible';
  return null;
}

function ReadinessCell({
  skuId,
  input,
  pending,
}: {
  readonly skuId: string;
  readonly input: ReadinessInput;
  readonly pending: boolean;
}): JSX.Element {
  const t = useT();
  // Missing bits are never evidence of readiness: three of the four
  // concerns are gateway-owned, so a pending or failed batch means
  // "unknown". Rendering "ready" off an absent answer would tell the
  // operator a SKU is sellable precisely when we could not check.
  if (input.bits === undefined) {
    return pending ? (
      <span className="text-paper-faint">…</span>
    ) : (
      <Chip tone="neutral" lamp={false} testId={`sku-market-readiness-${skuId}`}>
        {t('sku_market.readiness.unknown')}
      </Chip>
    );
  }
  const unmet = firstUnmetReadiness({ ...input, bits: input.bits });
  return unmet === null ? (
    <Chip tone="ok" testId={`sku-market-readiness-${skuId}`}>
      {t('sku_market.readiness.ready')}
    </Chip>
  ) : (
    <Chip tone="warn" testId={`sku-market-readiness-${skuId}`}>
      {t(unmet)}
    </Chip>
  );
}

function ProcurementCell({
  skuId,
  targets,
  index,
  pending,
}: {
  readonly skuId: string;
  readonly targets: readonly ProcurementTargetPin[] | undefined;
  readonly index: ProcurementCostIndex;
  /** True while the reads this cell depends on are still in flight. */
  readonly pending: boolean;
}): JSX.Element {
  const t = useT();
  const result = procurementRangeForTargets(targets, index);
  if (result.kind === 'unresolved') {
    // An em-dash is a claim — "this SKU has no procurement price" — so it
    // is reserved for the case where every bound target's upstream
    // answered and none carried one. A read that is pending or failed
    // gets the same treatment as the readiness chip's unknown state.
    return pending ? (
      <span data-testid={`sku-market-procurement-${skuId}`} className="text-paper-faint">
        …
      </span>
    ) : (
      <span data-testid={`sku-market-procurement-${skuId}`} className="text-warn">
        {t('sku_market.procurement.unknown')}
      </span>
    );
  }
  if (result.kind === 'absent') {
    return (
      <span data-testid={`sku-market-procurement-${skuId}`} className="text-paper-faint">
        —
      </span>
    );
  }
  const { range } = result;
  return (
    <span data-testid={`sku-market-procurement-${skuId}`}>
      {range.min === range.max
        ? formatMultiplier(range.min)
        : `${formatMultiplier(range.min)}–${formatRateMultiplier(range.max)}`}
    </span>
  );
}

export function SkuMarketPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const { status, user } = useSession();
  const channelsQ = useChannelsList();
  const skusQ = useChannelModelsList();
  const upstreamsQ = useUpstreamsList();
  const modelsQ = useModelsList();
  const [namespaceFilter, setNamespaceFilter] = useState<string>(ALL_NAMESPACES);
  const [newSkuOpen, setNewSkuOpen] = useState(false);

  const channels = useMemo(
    () => [...(channelsQ.data ?? [])].sort((a, b) => {
      const vendor = a.vendor.localeCompare(b.vendor);
      return vendor !== 0 ? vendor : a.slug.localeCompare(b.slug);
    }),
    [channelsQ.data],
  );
  const channelById = useMemo(
    () => new Map(channels.map((channel) => [channel.id, channel])),
    [channels],
  );
  const allSkus = useMemo(
    () => [...(skusQ.data ?? [])].sort((a, b) => a.sku_name.localeCompare(b.sku_name)),
    [skusQ.data],
  );
  const filteredSkus = useMemo(
    () => namespaceFilter === ALL_NAMESPACES
      ? allSkus
      : allSkus.filter((sku) => sku.channel_id === namespaceFilter),
    [allSkus, namespaceFilter],
  );
  // Batched over the whole catalog, not the visible partition: the
  // namespace tabs filter client-side, so re-reading per tab would buy
  // requests and nothing else.
  const skuIds = useMemo(() => allSkus.map((sku) => sku.sku_id), [allSkus]);
  const poolTargets = useRoutingDefaultSkuTargetsMap(skuIds);
  const readiness = useSkuReadiness(skuIds);

  const upstreamIds = useMemo(
    () => (upstreamsQ.data ?? []).map((upstream) => upstream.id),
    [upstreamsQ.data],
  );
  const procurement = useProcurementCostIndex(upstreamIds);
  // The cost fan-out is keyed off the upstream list, so an unloaded list
  // yields useQueries([]) — pending false, zero slices. Without folding the
  // list's own state in, the column would call itself settled before it had
  // anything to settle, and flash "no price" on the normal loading path.
  const procurementPending = upstreamsQ.isLoading
    || upstreamsQ.isPending
    || procurement.isPending
    || poolTargets.isPending;

  // The floor column reads one brand's sales costs. Brand handling mirrors
  // the server contract (see LeadPricingPage): admin sessions must name a
  // brand explicitly or the request 400s, while a lead sends none and lets
  // ops-api pin their profile brand. A session holding neither grant is
  // answered 403 — a missing column, not a broken page, so this query's
  // error is deliberately never raised into the page error state.
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);
  const salesCostsQ = useSalesCosts(
    isAdmin ? DEFAULT_BRAND_ID : null,
    status === 'authenticated',
  );
  // The brand the floor column is actually showing: the server echoes the
  // one it resolved (a lead's pinned profile brand need not be the default),
  // so prefer its answer over the request parameter.
  const salesCostBrand = salesCostsQ.data?.brand ?? null;
  const salesFloorBySku = useMemo(
    () => new Map(
      (salesCostsQ.data?.skus ?? []).map((entry) => [entry.sku_id, entry.sales_floor_multiplier]),
    ),
    [salesCostsQ.data],
  );

  if (channelsQ.isLoading || skusQ.isLoading) {
    return (
      <div data-testid="sku-market-loading" className="flex flex-col gap-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (channelsQ.error || skusQ.error) {
    return (
      <div
        data-testid="sku-market-error"
        className="rounded-lg border border-bad-line bg-bad-dim px-4 py-3 text-body text-bad-bright"
      >
        {coerceErrorMessage(channelsQ.error ?? skusQ.error)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('sku_market.title')}
        sub={t('sku_market.subtitle')}
        actions={
          <Button variant="primary" onClick={() => setNewSkuOpen(true)} data-testid="sku-market-new-btn">
            <Plus aria-hidden="true" className="h-4 w-4" />
            {t('sku_market.new_sku')}
          </Button>
        }
      />

      {/* The vendor catalog is the dialog's dependency, not the table's, so
          a failed models read must not take the marketplace list down with
          it — the dialog reports the outage itself and refuses to submit
          against a catalog it could not read. */}
      <NewSkuDialog
        open={newSkuOpen}
        onOpenChange={setNewSkuOpen}
        channels={channels}
        models={modelsQ.data ?? []}
        modelsPending={modelsQ.isPending}
        modelsError={modelsQ.error}
        existingSkus={allSkus}
      />

      {readiness.truncated ? (
        <div
          data-testid="sku-market-readiness-truncated"
          className="rounded-lg border border-warn-line bg-warn-dim px-4 py-3 text-dense text-warn"
        >
          {t('sku_market.readiness.truncated')}
        </div>
      ) : null}

      <Tabs value={namespaceFilter} onValueChange={setNamespaceFilter}>
        <TabsList ariaLabel={t('sku_market.namespace_filter_aria')} className="flex-wrap">
          <TabsTrigger value={ALL_NAMESPACES} testId="sku-market-filter-all">
            {t('sku_market.tabs.all')}
          </TabsTrigger>
          {channels.map((channel) => (
            <TabsTrigger
              key={channel.id}
              value={channel.id}
              testId={`sku-market-filter-${channel.id}`}
            >
              <span className="font-mono">{channel.slug}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {filteredSkus.length === 0 ? (
        <EmptyState testId="sku-market-empty" title={t('sku_market.empty')} />
      ) : (
        <Table testId="sku-market-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('sku_market.col.sku')}</Th>
              <Th>{t('sku_market.col.vendor')}</Th>
              <Th num>{t('sku_market.col.rate')}</Th>
              {/* The floor is per brand, and this page reads exactly one —
                  naming it keeps the number from reading as brand-agnostic.
                  A brand selector is deferred (#1335 review, low). */}
              <Th num>
                {salesCostBrand === null
                  ? t('sku_market.col.sales_cost')
                  : t('sku_market.col.sales_cost_brand', { brand: salesCostBrand })}
              </Th>
              <Th num>{t('sku_market.col.procurement_cost')}</Th>
              <Th>{t('sku_market.col.readiness')}</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {filteredSkus.map((sku: ChannelModelRecord) => {
              const channel = channelById.get(sku.channel_id);
              return (
                <Tr
                  key={sku.sku_id}
                  data-testid={`sku-market-row-${sku.sku_id}`}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/market/${encodeURIComponent(sku.sku_id)}`)}
                >
                  <Td>
                    <div className="flex flex-col gap-0.5">
                      <span className="font-mono text-body font-medium text-paper">{sku.sku_name}</span>
                      <span className="font-mono text-micro text-paper-low">{sku.sku_id}</span>
                    </div>
                  </Td>
                  <Td>
                    <Chip tone="neutral" lamp={false} className="font-mono">
                      {channel?.vendor ?? '—'}
                    </Chip>
                  </Td>
                  <Td num emphasis data-testid={`sku-market-rate-${sku.sku_id}`}>
                    {formatMultiplier(sku.rate_multiplier)}
                  </Td>
                  <Td num data-testid={`sku-market-sales-cost-${sku.sku_id}`}>
                    {formatMultiplier(salesFloorBySku.get(sku.sku_id))}
                  </Td>
                  <Td num>
                    <ProcurementCell
                      skuId={sku.sku_id}
                      targets={poolTargets.targetsBySku.get(sku.sku_id)}
                      index={procurement}
                      pending={procurementPending}
                    />
                  </Td>
                  <Td>
                    <ReadinessCell
                      skuId={sku.sku_id}
                      pending={readiness.isPending}
                      input={{
                        priced: sku.priced,
                        visible: sku.org_visibility_default,
                        bits: readiness.bitsBySku.get(sku.sku_id),
                      }}
                    />
                  </Td>
                  <Td className="text-right">
                    <ChevronRight
                      aria-hidden="true"
                      className="h-4 w-4 text-paper-low transition-colors duration-120 group-hover:text-signal"
                    />
                  </Td>
                </Tr>
              );
            })}
          </TBody>
        </Table>
      )}
    </div>
  );
}
